import { FC, useState } from 'react';
import styled from 'styled-components';
import SwiperClass from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';

import PartnerSlide from '@/components/StoriesSlider/PartnerSlide';
import { getImage } from '@/lib/fetchAPI';
import { parseHTML } from '@/lib/parse';
import { translate } from '@/lib/translate';

const Partner: FC<any> = ({ lang, data }) => {
	const [swiper, setSwiper] = useState<SwiperClass | null>(null);

	const orderedCountries = data?.countries?.sort(
		(a, b) => a?.url?.toLowerCase() - b?.url?.toLowerCase(),
	);

	const cleanUrl = (url?: string) =>
		url
			?.trim()
			?.replace(/^https?:\/\//, '')
			?.replace(/\/$/, '');

	const handleSlideChange = (direction: string) =>
		direction === 'next' ? swiper?.slideNext() : swiper?.slidePrev();

	return (
		<Wrapper>
			<Left>
				<a
					href={'/' + lang + '/messaging-partners'}
					className="partner-close-popup-button"
				>
					<img alt="Close" src="/images/messaging-partners/close.svg" />

					<span>
						{translate(lang, { en: 'Close', ru: 'Закрыть', ua: 'Закрити' })}
					</span>
				</a>
			</Left>

			<Container>
				<Heading>
					<Logo
						loading="lazy"
						alt={data?.partner?.company}
						src={getImage(data?.partner?.logo)}
					/>

					<Title>{parseHTML(data?.partner?.company)}</Title>
				</Heading>

				<Content>
					<ContentTitle className="mobile-only">
						{translate(lang, {
							en: 'Description',
							ua: 'Опис',
							ru: 'Описание',
						})}
					</ContentTitle>

					{parseHTML(data?.partner?.longDescription ?? '')}

					<InfoContent>
						<Info>
							<img
								alt="Location"
								src="/images/messaging-partners/location.svg"
							/>

							<InfoTextBlack>{data?.partner?.mainLocation}</InfoTextBlack>
						</Info>

						<Info>
							<img alt="Link" src="/images/messaging-partners/link.svg" />

							<InfoText
								target="_blank"
								rel="noopener noreferrer"
								href={data?.partner?.website}
								onClick={() => {
									// @ts-ignore
									gtag('event', 'click_partner_website');
								}}
							>
								{cleanUrl(data?.partner?.website)}
							</InfoText>
						</Info>

						<Info>
							<img alt="Email" src="/images/messaging-partners/email.svg" />

							<InfoText
								target={data?.partner?.contactFormLink ? '_blank' : undefined}
								href={
									data?.partner?.contactFormLink ??
									'mailto:' + data?.partner?.email
								}
								onClick={() => {
									// @ts-ignore
									gtag('event', 'click_partner_email');
								}}
							>
								{data?.partner?.contactFormLink
									? cleanUrl(data?.partner?.contactFormLink)
									: data?.partner?.email}
							</InfoText>
						</Info>
					</InfoContent>
				</Content>

				{data?.integrations ? (
					<Content>
						<ContentTitle>
							{translate(lang, {
								en: 'Services provided',
								ua: 'Надані послуги',
								ru: 'Предоставляемые услуги',
							})}
						</ContentTitle>

						<ServicesWrapper>
							{data?.integrations?.map((item, index) => (
								<Service key={`partner_integration_${index}`}>
									<ServiceTitle>{item?.integration}</ServiceTitle>
								</Service>
							))}

							{data?.partner?.servicesProvided?.map((item, index) => (
								<Service key={`service_provided_${index}`}>
									<ServiceTitle>{item?.service}</ServiceTitle>
								</Service>
							))}
						</ServicesWrapper>
					</Content>
				) : null}

				{data?.stories?.length > 0 && (
					<Content>
						<SuccessStoriesHeader>
							<ContentTitle>
								{translate(lang, {
									en: 'Success Stories',
									ua: 'Історії успіху',
									ru: 'Истории успеха',
								})}
							</ContentTitle>

							{data?.stories?.length > 1 && (
								<Arrows>
									<Arrow onClick={() => handleSlideChange('prev')}>
										<img
											alt="Left Arrow"
											src="/images/icons/slider/left-arrow.svg"
										/>
									</Arrow>

									<Arrow onClick={() => handleSlideChange('next')}>
										<img
											alt="Right Arrow"
											src="/images/icons/slider/right-arrow.svg"
										/>
									</Arrow>
								</Arrows>
							)}
						</SuccessStoriesHeader>

						<SuccessStoriesContent>
							{data?.stories?.length > 1 ? (
								<>
									<Arrows className="mobile-only">
										<Arrow onClick={() => handleSlideChange('prev')}>
											<img
												alt="Left Arrow"
												src="/images/icons/slider/left-arrow.svg"
											/>
										</Arrow>

										<Arrow onClick={() => handleSlideChange('next')}>
											<img
												alt="Right Arrow"
												src="/images/icons/slider/right-arrow.svg"
											/>
										</Arrow>
									</Arrows>

									<Swiper
										loop
										spaceBetween={40}
										slidesPerView={1}
										onSwiper={setSwiper}
									>
										{data?.stories.map((story, index) => (
											<SwiperSlide key={`partner_story_${index}`}>
												<PartnerSlide isMultiple slide={story} />
											</SwiperSlide>
										))}
									</Swiper>
								</>
							) : (
								<PartnerSlide slide={data?.stories?.[0]} />
							)}
						</SuccessStoriesContent>
					</Content>
				)}

				{orderedCountries ? (
					<Content>
						<ContentTitle>
							{translate(lang, {
								en: 'Countries of operation',
								ua: 'Країни присутності',
								ru: 'Страны присутствия',
							})}
						</ContentTitle>

						<Text>
							{orderedCountries?.map((item, index: number) => (
								<span key={`ordered_country_${index}`}>
									{item?.country +
										(index !== (data?.countries?.length ?? 1) - 1 ? ', ' : ' ')}
								</span>
							))}
						</Text>
					</Content>
				) : null}

				{data?.industries ? (
					<Content>
						<ContentTitle>
							{translate(lang, {
								en: 'Covered industries',
								ua: 'Охоплені галузі',
								ru: 'Охватываемые отрасли',
							})}
						</ContentTitle>

						<Text>
							{data?.industries?.map((item, index: number) => (
								<span key={`partner_industry_${index}`}>
									{item?.industry +
										(index !== data?.industries?.length - 1 ? ', ' : ' ')}
								</span>
							))}
						</Text>
					</Content>
				) : null}
			</Container>
		</Wrapper>
	);
};

const Wrapper = styled.div`
	left: 0;
	top: 0;
	width: 100%;
	height: 100%;
	overflow: auto;
	background-color: var(--color-white-30);
	position: fixed;
	padding-bottom: 80px;
	padding-top: 59px;
	z-index: 99999;

	@media (width <= 768px) {
		padding-top: 30px;
		padding-bottom: 65px;
	}
`;

const Container = styled.div`
	width: 50%;
	margin: 0 auto;

	@media (width <= 992px) {
		width: 70%;
	}

	@media (width <= 576px) {
		width: 100%;
		padding: 0 15px;
	}
`;

const Left = styled.div`
	width: 90%;
	margin: 0 auto;
`;

const Heading = styled.div`
	padding-bottom: 30px;

	@media (width <= 768px) {
		border-bottom: 1px solid var(--color-purple-opacity-30);
		margin-bottom: 30px;
	}
`;

const Text = styled.div`
	line-height: 1.5rem;
	text-transform: capitalize;
`;

const SuccessStoriesContent = styled.div`
	position: relative;
`;

const Content = styled.div`
	padding-bottom: 40px;
	border-bottom: 1px solid var(--color-purple-opacity-30);

	@media (width <= 768px) {
		padding-bottom: 30px;
	}

	h4 {
		font-size: 1.75rem;
		line-height: 1.5rem;
		color: var(--color-black);
		margin-bottom: 30px;
		margin-top: 40px;

		@media (width <= 768px) {
			font-size: 1.375rem;
			line-height: 1.75rem;
			margin-top: 30px;
			margin-bottom: 20px;
		}
	}

	p:not(${SuccessStoriesContent} p) {
		font-size: 1.125rem;
		line-height: 1.5rem;
		color: var(--color-black);
		margin-bottom: 15px;

		@media (width <= 768px) {
			font-size: 1rem;
			line-height: 1.5rem;
			margin-bottom: 12px;
		}
	}

	p > a {
		color: var(--color-purple);
	}

	${Text}:not(${SuccessStoriesContent} p) {
		margin-bottom: 0;
	}

	ul,
	ol {
		width: 100%;
		padding-left: 15px;
		margin-bottom: 12px;
	}

	li {
		font-family: var(--font-sans);

		font-size: 18px;
		line-height: 24px;
		color: var(--color-black);
		padding-left: 2px;
		position: relative;
		list-style-type: disc;

		@media (max-width: 767px) {
			font-size: 16px;
			line-height: 20px;
		}
	}
`;

const ContentTitle = styled.h4`
	&.mobile-only {
		display: none;

		@media (width <= 768px) {
			display: block;
		}
	}
`;

const SuccessStoriesHeader = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;

	@media (width >= 769px) {
		margin: 40px 0 30px;

		${ContentTitle} {
			margin: 0;
		}
	}
`;

const ServicesWrapper = styled.div`
	display: flex;
	flex-wrap: wrap;
	justify-content: space-between;
	gap: 8px 40px;

	@media (width <= 768px) {
		flex-direction: column;
		gap: 16px;
	}
`;

const Service = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	width: calc(50% - 20px);

	@media (width <= 768px) {
		width: 100%;
		gap: 10px;
	}
`;

const ServiceTitle = styled.h3`
	font-size: 1.125rem;
	line-height: 1.5rem;
	margin-top: 2px;
	color: var(--color-black);

	@media (width <= 768px) {
		font-size: 1rem;
		margin-top: 4px;
	}
`;

const Logo = styled.img`
	max-width: 100%;
	max-height: 100px;
	margin-bottom: 30px;

	@media (width <= 768px) {
		max-height: 60px;
		margin-bottom: 20px;
	}
`;

const Title = styled.h3`
	color: var(--color-purple);
	font-family: var(--font-sans);
	font-weight: var(--font-bold);
	font-size: 2.5rem;
	line-height: 3rem;
`;

const Info = styled.div`
	margin-top: 20px;
	display: flex;
	align-items: center;

	@media (width <= 768px) {
		margin-top: 10px;
	}
`;

const InfoContent = styled.div`
	margin-top: 30px;

	@media (width <= 768px) {
		margin-top: 20px;
	}
`;

const InfoText = styled.a`
	font-size: 1.125rem;
	line-height: 1.5rem;
	color: var(--color-purple);
	margin-left: 18px;
	text-decoration: none;

	@media (width <= 768px) {
		font-size: 1rem;
		margin-left: 15px;
	}

	&:hover {
		text-decoration: underline;
	}
`;

const InfoTextBlack = styled.a`
	color: black;

	font-size: 1.125rem;
	line-height: 1.5rem;
	margin-left: 18px;

	@media (width <= 768px) {
		font-size: 1rem;
		margin-left: 15px;
	}
`;

const Arrows = styled.div`
	display: flex;
	gap: 12px;
	pointer-events: none;

	@media (width <= 768px) {
		display: none;
	}

	&.mobile-only {
		display: none;
		position: absolute;
		bottom: 18px;

		@media (width <= 768px) {
			display: flex;
			justify-content: space-between;
			z-index: 10;
			width: 100%;
		}
	}
`;

const Arrow = styled.button`
	display: flex;
	align-items: center;
	justify-content: center;
	width: 50px;
	height: 50px;
	background: linear-gradient(
		180deg,
		var(--color-purple) 0%,
		var(--color-purple-80) 100%
	);
	border-radius: 50%;
	cursor: pointer;
	pointer-events: auto;

	img {
		width: 40px;
		height: 40px;
	}
`;

export default Partner;
